/**
 * A largura de uma coluna ou barra lateral que a pessoa arrasta pela borda.
 *
 * Serve aos dois lados da tela: a barra que fica à **direita** arrasta pela
 * borda esquerda (arrastar para a esquerda alarga) e a coluna que fica à
 * **esquerda**, pela borda direita. Dois cliques na alça voltam ao padrão, e as
 * setas do teclado mexem de 24 em 24 px com a alça em foco.
 *
 * **A largura é lembrada por `chave`**, no navegador — quem alargou para ler não
 * quer alargar de novo a cada visita. Sem armazenamento (aba anônima), funciona
 * igual e só esquece.
 */

import { useRef, useState, type ReactNode } from "react"

function limitar(n: number, minimo: number, maximo: number) {
  // Nunca a ponto de esconder a tela de trás: sobra pelo menos 240 px dela.
  const teto = Math.min(maximo, Math.max(minimo, window.innerWidth - 240))
  return Math.round(Math.min(teto, Math.max(minimo, n)))
}

export function usarLarguraArrastavel({ chave, padrao, minimo = 300, maximo = 1100, inicial }: {
  chave: string
  padrao: number
  minimo?: number
  maximo?: number
  /** Usada quando nada foi guardado ainda nesta chave — por exemplo, a largura de antes. */
  inicial?: number
}) {
  const [largura, setLargura] = useState(() => {
    try {
      const n = Number(localStorage.getItem(`largura:${chave}`))
      if (n > 0) return limitar(n, minimo, maximo)
    } catch { /* sem armazenamento */ }
    return limitar(inicial ?? padrao, minimo, maximo)
  })
  const [arrastando, setArrastando] = useState(false)

  function gravar(n: number) {
    const v = limitar(n, minimo, maximo)
    setLargura(v)
    try { localStorage.setItem(`largura:${chave}`, String(v)) } catch { /* sem armazenamento */ }
  }

  return { largura, gravar, padrao, minimo, maximo, arrastando, setArrastando }
}

/**
 * Uma barra lateral **encaixada** à direita: ocupa o lado da área de trabalho, sem
 * cobrir o cabeçalho nem as faixas de cima e sem escurecer o resto — dá para
 * continuar mexendo no desenho com ela aberta.
 *
 * A alça fica na borda, fora da parte que rola: rolando o conteúdo, ela não sai
 * do lugar.
 */
export function BarraLateral({ chave, padrao, rotulo, children }: {
  chave: string
  padrao: number
  rotulo: string
  children: ReactNode
}) {
  const largura = usarLarguraArrastavel({ chave, padrao })
  return (
    <aside aria-label={rotulo} style={{
      flex: `0 0 ${largura.largura}px`, width: largura.largura, minHeight: 0, minWidth: 0,
      position: "relative", display: "flex", flexDirection: "column",
      background: "var(--surface)", borderLeft: "1px solid var(--line2)",
      userSelect: largura.arrastando ? "none" : undefined,
    }}>
      <AlcaDeLargura {...largura} rotulo={`Largura: ${rotulo.toLowerCase()}`} />
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "12px 14px",
                    display: "flex", flexDirection: "column", gap: 10 }}>
        {children}
      </div>
    </aside>
  )
}

export function AlcaDeLargura({
  largura, gravar, padrao, minimo, maximo, arrastando, setArrastando,
  borda = "esquerda", rotulo = "Largura da barra lateral",
}: ReturnType<typeof usarLarguraArrastavel> & {
  /** Em que borda da coluna fica a alça: `esquerda` para a barra da direita da tela. */
  borda?: "esquerda" | "direita"
  rotulo?: string
}) {
  const inicio = useRef<{ x: number; largura: number } | null>(null)
  // Para que lado do mouse a coluna cresce.
  const sinal = borda === "esquerda" ? -1 : 1

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`${rotulo} — arraste, ou use as setas`}
      aria-valuenow={largura}
      aria-valuemin={minimo}
      aria-valuemax={maximo}
      tabIndex={0}
      title="Arraste para mudar a largura · dois cliques voltam ao padrão"
      onPointerDown={(ev) => {
        ev.preventDefault()
        ev.currentTarget.setPointerCapture?.(ev.pointerId)
        inicio.current = { x: ev.clientX, largura }
        setArrastando(true)
      }}
      onPointerMove={(ev) => {
        if (!inicio.current) return
        gravar(inicio.current.largura + sinal * (ev.clientX - inicio.current.x))
      }}
      onPointerUp={() => { inicio.current = null; setArrastando(false) }}
      onPointerCancel={() => { inicio.current = null; setArrastando(false) }}
      onDoubleClick={() => gravar(padrao)}
      onKeyDown={(ev) => {
        if (ev.key === "ArrowLeft") { ev.preventDefault(); gravar(largura - sinal * 24) }
        if (ev.key === "ArrowRight") { ev.preventDefault(); gravar(largura + sinal * 24) }
      }}
      className="alca-coluna"
      style={{
        position: "absolute", top: 0, bottom: 0, width: 8, zIndex: 5,
        ...(borda === "esquerda" ? { left: -4 } : { right: -4 }),
        cursor: "col-resize", touchAction: "none",
        background: arrastando ? "var(--acao)" : "transparent",
        opacity: arrastando ? 0.35 : 1,
      }}
    />
  )
}
